import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();

// Значения по умолчанию
const defaultColors = {
  light: {
    primary: '#3f69ff',
    bgPrimary: '#f8fafc',
    bgSecondary: '#eef2ff',
    textPrimary: '#0f172a',
    textSecondary: '#64748b'
  },
  dark: {
    primary: '#6c7bff',
    bgPrimary: '#050816',
    bgSecondary: '#0f172a',
    textPrimary: '#e2e8f0',
    textSecondary: '#94a3b8'
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [themeColors, setThemeColors] = useState(() => {
    const saved = localStorage.getItem('themeColors');
    return saved ? JSON.parse(saved) : defaultColors;
  });

  // Загрузка цветов темы с сервера
  useEffect(() => {
    const fetchThemeColors = async () => {
      try {
        const res = await fetch('/api/public/system-settings');
        if (res.ok) {
          const data = await res.json();
          const newColors = {
            light: {
              primary: data.theme_primary_color || defaultColors.light.primary,
              bgPrimary: data.theme_bg_primary || defaultColors.light.bgPrimary,
              bgSecondary: data.theme_bg_secondary || defaultColors.light.bgSecondary,
              textPrimary: data.theme_text_primary || defaultColors.light.textPrimary,
              textSecondary: data.theme_text_secondary || defaultColors.light.textSecondary
            },
            dark: {
              primary: data.theme_primary_color_dark || defaultColors.dark.primary,
              bgPrimary: data.theme_bg_primary_dark || defaultColors.dark.bgPrimary,
              bgSecondary: data.theme_bg_secondary_dark || defaultColors.dark.bgSecondary,
              textPrimary: data.theme_text_primary_dark || defaultColors.dark.textPrimary,
              textSecondary: data.theme_text_secondary_dark || defaultColors.dark.textSecondary
            }
          };
          setThemeColors(newColors);
          localStorage.setItem('themeColors', JSON.stringify(newColors));
        }
      } catch (e) {
        console.error('Ошибка загрузки цветов темы:', e);
      }
    };
    fetchThemeColors();
  }, []);

  // Применение темы и цветов
  useEffect(() => {
    const body = window.document.body;
    const root = document.documentElement;
    
    // Применяем класс темы
    body.classList.remove('light', 'dark');
    body.classList.add(theme);
    localStorage.setItem('theme', theme);
    
    // Получаем цвета текущей темы
    const colors = theme === 'light' ? themeColors.light : themeColors.dark;
    
    // Применяем все цвета
    root.style.setProperty('--accent-primary', colors.primary);
    root.style.setProperty('--accent-primary-hover', colors.primary);
    root.style.setProperty('--bg-primary', colors.bgPrimary);
    root.style.setProperty('--bg-secondary', colors.bgSecondary);
    root.style.setProperty('--text-primary', colors.textPrimary);
    root.style.setProperty('--text-secondary', colors.textSecondary);
    root.style.setProperty('--text-link', colors.primary);
    root.style.setProperty('--surface-strong', `${colors.primary}20`);
    root.style.setProperty('--accent-color', themeColors.dark.primary);
    
    // Градиентный фон
    root.style.setProperty('--body-bg', theme === 'light' 
      ? `linear-gradient(180deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 35%, ${colors.bgSecondary} 100%)`
      : `radial-gradient(circle at 20% 20%, ${colors.primary}40, transparent 45%), ${colors.bgPrimary}`
    );
    
    // Glass эффекты
    root.style.setProperty('--glass-bg', theme === 'light' 
      ? `${colors.bgPrimary}f2`
      : `${colors.bgSecondary}eb`
    );
  }, [theme, themeColors]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Функция для обновления цветов
  const updateThemeColors = (lightPrimary, darkPrimary) => {
    // Простое обновление - перезагружаем настройки с сервера
    fetch('/api/public/system-settings')
      .then(res => res.json())
      .then(data => {
        const newColors = {
          light: {
            primary: data.theme_primary_color || defaultColors.light.primary,
            bgPrimary: data.theme_bg_primary || defaultColors.light.bgPrimary,
            bgSecondary: data.theme_bg_secondary || defaultColors.light.bgSecondary,
            textPrimary: data.theme_text_primary || defaultColors.light.textPrimary,
            textSecondary: data.theme_text_secondary || defaultColors.light.textSecondary
          },
          dark: {
            primary: data.theme_primary_color_dark || defaultColors.dark.primary,
            bgPrimary: data.theme_bg_primary_dark || defaultColors.dark.bgPrimary,
            bgSecondary: data.theme_bg_secondary_dark || defaultColors.dark.bgSecondary,
            textPrimary: data.theme_text_primary_dark || defaultColors.dark.textPrimary,
            textSecondary: data.theme_text_secondary_dark || defaultColors.dark.textSecondary
          }
        };
        setThemeColors(newColors);
        localStorage.setItem('themeColors', JSON.stringify(newColors));
      })
      .catch(e => console.error('Ошибка обновления цветов:', e));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, themeColors, updateThemeColors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  return useContext(ThemeContext);
};